import { jsx, jsxs } from 'react/jsx-runtime';
import { composeEventHandlers } from '@radix-ui/primitive';
import { useComposedRefs } from '@radix-ui/react-compose-refs';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import classNames from 'classnames';
import { Command, CommandList } from 'cmdk';
import { forwardRef, useMemo } from 'react';
import usePopperPresence from '../../../../../hooks/usePopperPresence.mjs';
import { usePopperZIndexStyle } from '../../../../../hooks/usePopperZIndexStyle.mjs';
import { usePreventTransitionInteraction } from '../../../../../hooks/usePreventTransitionInteraction.mjs';
import { getComputedVerticalCollisionPadding } from '../../../../../utils/collision-padding.mjs';
import { PopoverPortal, PopoverContent } from '../../../../popover/patched-popover/PatchedPopover.mjs';
import { ScrollArea } from '../../../../scroll-area/ScrollArea.mjs';
import { SELECT_CONTENT_DEFAULT_COLLISION_PADDING, SELECT_CONTENT_DEFAULT_SIDE_OFFSET } from '../../../shared/constants/style.mjs';
import { content, contentVariants, contentCommandRoot, contentScrollAreaViewport } from '../../../shared/styles/content.vanilla.mjs';
import { optionList } from '../../../shared/styles/optionList.vanilla.mjs';
import { selectContentFooterHeight, selectContentAffixHeight, selectContentExternalMaxHeightSize, selectContentVerticalCollisionPaddingSize } from '../../../shared/styles/variables.vanilla.mjs';
import { useSingleSelectContext } from '../../contexts/context.mjs';

const SingleSelectContent = forwardRef(
  ({
    children,
    className: classNameFromProps,
    align = "start",
    affix,
    collisionPadding = SELECT_CONTENT_DEFAULT_COLLISION_PADDING,
    sideOffset = SELECT_CONTENT_DEFAULT_SIDE_OFFSET,
    footer,
    fitToTrigger,
    prefersReducedMotion,
    maxHeight,
    portalContainer,
    style: styleFromProps,
    zIndex,
    onKeyDownCapture,
    ...props
  }, forwardedRef) => {
    const {
      open,
      value,
      contentAffixElement,
      contentFooterElement,
      loop,
      filter,
      shouldFilter,
      onSearchKeywordChange,
      onClose
    } = useSingleSelectContext("Content");
    const preventTransitionInteraction = usePreventTransitionInteraction({
      open
    });
    const popperZIndexStyle = usePopperZIndexStyle(open, zIndex);
    const { ref: presenceRef } = usePopperPresence({
      open,
      onPresenceChange: (present) => {
        if (!present) {
          onSearchKeywordChange("");
          onClose?.();
        }
      }
    });
    const composedRef = useComposedRefs(forwardedRef, presenceRef);
    const style = useMemo(() => {
      const computedVerticalCollisionPadding = getComputedVerticalCollisionPadding({
        collisionPadding,
        fallbackCollisionPadding: SELECT_CONTENT_DEFAULT_COLLISION_PADDING
      });
      const externalMaxHeight = typeof maxHeight === "number" ? `${maxHeight}px` : maxHeight;
      const contentAffixHeight = contentAffixElement?.clientHeight ?? 0;
      const contentFooterHeight = contentFooterElement?.clientHeight ?? 0;
      return Object.assign(
        {},
        assignInlineVars({
          [selectContentVerticalCollisionPaddingSize]: `${computedVerticalCollisionPadding}px`,
          [selectContentExternalMaxHeightSize]: externalMaxHeight ?? null,
          [selectContentAffixHeight]: `${contentAffixHeight}px`,
          [selectContentFooterHeight]: `${contentFooterHeight}px`
        }),
        popperZIndexStyle,
        preventTransitionInteraction.style,
        styleFromProps
      );
    }, [
      collisionPadding,
      contentAffixElement?.clientHeight,
      contentFooterElement?.clientHeight,
      maxHeight,
      popperZIndexStyle,
      preventTransitionInteraction.style,
      styleFromProps
    ]);
    const className = classNames(
      content,
      contentVariants({ fitToTrigger, prefersReducedMotion }),
      classNameFromProps
    );
    const handleKeyDownCapture = composeEventHandlers(
      onKeyDownCapture,
      preventTransitionInteraction.onKeyDownCapture,
      { checkForDefaultPrevented: false }
    );
    return /* @__PURE__ */ jsx(PopoverPortal, { container: portalContainer, children: /* @__PURE__ */ jsx(
      Command,
      {
        "data-scope": "single-select",
        "data-part": "command-root",
        defaultValue: value,
        className: contentCommandRoot,
        loop,
        filter,
        shouldFilter,
        children: /* @__PURE__ */ jsxs(
          PopoverContent,
          {
            "data-scope": "single-select",
            "data-part": "content",
            align,
            collisionPadding,
            className,
            sideOffset,
            style,
            onKeyDownCapture: handleKeyDownCapture,
            ref: composedRef,
            ...props,
            children: [
              affix,
              /* @__PURE__ */ jsx(ScrollArea, { "data-scope": "single-select", "data-part": "scroll-area", children: /* @__PURE__ */ jsx(
                ScrollArea.Viewport,
                {
                  "data-scope": "single-select",
                  "data-part": "scroll-area-viewport",
                  className: contentScrollAreaViewport,
                  children: /* @__PURE__ */ jsx(
                    CommandList,
                    {
                      "aria-multiselectable": false,
                      "data-scope": "single-select",
                      "data-part": "content-option-list",
                      className: optionList,
                      children
                    }
                  )
                }
              ) }),
              footer
            ]
          }
        )
      }
    ) });
  }
);

export { SingleSelectContent as SelectContent };
//# sourceMappingURL=Content.mjs.map
